import React from 'react'
import { useThemeColors } from '../../../hooks/useThemeColors'
import { 
  SparklesIcon, 
  ClockIcon, 
  CheckCircleIcon,
  RocketLaunchIcon 
} from '@heroicons/react/24/outline'

export default function WelcomeStep() {
  const colors = useThemeColors()

  const features = [
    {
      icon: SparklesIcon,
      title: 'Smart Templates',
      description: 'Pre-configured options based on your business type'
    },
    {
      icon: ClockIcon,
      title: '10-15 Minutes',
      description: 'Get your facility up and running quickly'
    },
    {
      icon: CheckCircleIcon,
      title: 'Skip Anytime',
      description: 'Optional steps can be completed later'
    },
    {
      icon: RocketLaunchIcon,
      title: 'Ready to Launch',
      description: 'Start accepting bookings immediately after setup'
    }
  ]

  return (
    <div className="welcome-step">
      {/* Hero Section */}
      <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
        <h1 style={{
          fontSize: '2.5rem',
          fontWeight: '700',
          marginBottom: '1rem',
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryHover} 100%)`,
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          Welcome to Facility Manager Pro!
        </h1>
        <p style={{
          fontSize: '1.125rem',
          color: colors.textSecondary,
          maxWidth: '600px',
          margin: '0 auto',
          lineHeight: '1.6'
        }}>
          Let's get your facility set up in just a few simple steps. 
          This wizard will guide you through everything you need to start managing bookings, members, and more.
        </p>
      </div>

      {/* Features Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1.5rem',
        marginBottom: '3rem'
      }}>
        {features.map((feature, index) => {
          const Icon = feature.icon
          return (
            <div
              key={index}
              style={{
                padding: '1.5rem',
                backgroundColor: colors.background,
                borderRadius: '8px',
                border: `1px solid ${colors.border}`,
                textAlign: 'center',
                transition: 'transform 0.2s ease',
                cursor: 'default'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
              }}
            >
              <div style={{
                width: '48px',
                height: '48px',
                margin: '0 auto 1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '12px',
                backgroundColor: `${colors.primary}20`
              }}>
                <Icon style={{ 
                  width: '24px', 
                  height: '24px', 
                  color: colors.primary 
                }} />
              </div>
              <h3 style={{
                fontSize: '1rem',
                fontWeight: '600',
                marginBottom: '0.5rem',
                color: colors.text
              }}>
                {feature.title}
              </h3>
              <p style={{
                fontSize: '0.875rem',
                color: colors.textSecondary,
                margin: 0
              }}>
                {feature.description}
              </p>
            </div>
          )
        })}
      </div>

      {/* What We'll Set Up */}
      <div style={{
        backgroundColor: colors.background,
        borderRadius: '8px',
        border: `1px solid ${colors.border}`,
        padding: '2rem'
      }}>
        <h2 style={{
          fontSize: '1.25rem',
          fontWeight: '600',
          marginBottom: '1rem',
          color: colors.text
        }}>
          What we'll set up together:
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1rem'
        }}>
          {[
            '✅ Your business information & hours',
            '✅ Logo and brand colors',
            '✅ Facilities and spaces',
            '✅ Services and pricing',
            '✅ Membership tiers (optional)',
            '✅ Team member invitations (optional)'
          ].map((item, index) => (
            <div key={index} style={{
              fontSize: '0.875rem',
              color: colors.textSecondary
            }}>
              {item}
            </div>
          ))}
        </div>
      </div>

      {/* Note */}
      <div style={{
        marginTop: '2rem',
        padding: '1rem',
        backgroundColor: `${colors.primary}10`,
        borderRadius: '8px',
        border: `1px solid ${colors.primary}30`,
        textAlign: 'center'
      }}>
        <p style={{
          fontSize: '0.875rem',
          color: colors.text,
          margin: 0
        }}>
          💡 <strong>Tip:</strong> Have your business information, logo, and service list ready for the smoothest setup experience.
        </p>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .welcome-step h1 {
            font-size: 2rem !important;
          }

          .welcome-step p {
            font-size: 1rem !important;
          }
        }
      `}</style>
    </div>
  )
}